import React from 'react';
import './App.css';
import {
    createBrowserRouter,
    Route,
    RouterProvider,
    createRoutesFromElements,
    redirect,
    Navigate
} from "react-router-dom";

import ErrorPage from "./error-page";
import {HelmetProvider} from 'react-helmet-async';
import Menu, {loader as MenuLoader} from "./routes/menu";
import NoMenuLayout from "./routes/noMenuLayout";
import CalendarPage, {loader as CalendarLoader} from './routes/calendar/calendar';
import NewObject, {loader as NewObjectLoader, action as NewObjectAction} from "./routes/new-object";
import Owners, {loader as OwnersLoader} from "./routes/owners";
import Tariffs, {loader as TariffLoader} from './routes/tariffs/tariffs';
import Restrictions from './routes/restrictions';
import Objects from './routes/objects';
import MobileList, {loader as MobileListLoader} from './routes/mobile/objectsList';
import {createTheme, ThemeProvider} from '@mui/material';
import MobileMenu from './routes/mobile/menu';
import ObjectSingle, {loader as SingleObjectLoader} from './routes/mobile/objectSingle';
import OwnersMobile, {loader as OwnersMobileLoader} from './routes/mobile/owners';
import Profile, {loader as ProfileLoader} from './routes/profile';
import AdminProfile, {loader as AdminLoader} from './routes/admin';
import NewUser, {action as NewUserAction, loader as NewUserLoader} from './routes/profile/new-user';
import NewMail, {action as NewMailAction, loader as NewMailLoader} from './routes/profile/new-mail';
import AllUsers, {loader as AllUsersLoader} from './routes/profile/all-users';
import AllMailboxes, {loader as AllMailboxesLoader} from './routes/profile/all-mailboxes';
import UserRights, {loader as UserRightsLoader, action as UserRightsAction} from './routes/profile/rights';
import ProfileDetails, {loader as ProfileDetailsLoader, action as ProfileDetailsAction} from './routes/profile/details';
import LoginPage, {action as LoginPageAction, loader as LoginPageLoader} from './routes/login';
import Page404 from './404';
import DocsViewer, {loader as DocsLoaderPage} from "./routes/docs/DocsViewer";
import FrontDocsViewer, {loader as FrontDocsLoaderPage} from "./routes/docs/FrontDocsViewer";
import {loader as ApiDocsLoaderPage} from "./routes/docs/RedocViewer";
import SingleObjectLayout, {loader as SingleObjectLayoutLoader} from './routes/object/layout';
import OrdersPage, {mutateOrder, action as OrdersAction, loader as OrdersLoader} from './routes/ordersPage';
import {loader as DocsLoader} from './routes/docs';
import ObjectDraft, {loader as ObjectDraftLoader, action as ObjectDraftAction} from './routes/newObjects/edit';
import EditChildObj, {loader as EditChildObjLoader, action as EditChildObjAction} from './routes/newObjects/child_edit';
import AddLocationToObj, {
    loader as AddLocationLoader,
    action as AddLocationAction
} from './routes/newObjects/location_edit';
import EditUserDetails, {
    loader as EditUserDetailsLoader,
    action as EditUserDetailsAction
} from './routes/profile/all-usersActions/edit';
import EditUserRights, {
    loader as EditUserRightsLoader,
    action as EditUserRightsAction
} from './routes/profile/all-usersActions/rights';
import PublicationPage from './routes/admin/publication';
import ForbiddenPage from './routes/forbidden';
import {requirePartnerOrSuperAdmin, requireSuperAdmin} from './auth/guards';

import TestPage, {loader as TestPageLoader} from './routes/testPage';
import ObjectDetails from './routes/object/pages/details/details';
import ObjectTariffs from './routes/object/pages/tariffs/tariffs';
import ObjectRules from './routes/object/pages/rules/rules';
import ObjectChildrenTarrifs from './routes/object/pages/children_tariffs/children_tariffs';
import ObjectComfortables from './routes/object/pages/comfortables';
import ObjectUserRights from './routes/object/pages/user_rights';
import ErrorBoundary from './components/errorBoundary';
import AuthProvider from './hooks/authProvider';
import ObjectCalendar from './routes/object/pages/calendar';
import ObjectPhotos from './routes/object/pages/photos/photos';
import EditObjectPhotos, {
    loader as EditObjectPhotosLoader,
    action as EditObjectPhotosAction
} from './routes/object/pages/photos/edit_photos';
import NewTariff, {loader as NewTariffLoader, action as NewTariffAction} from './routes/tariffs/new_tariff';
import EditChildObjectPhotos, {
    loader as EditChildObjectPhotosLoader,
    action as EditChildObjectPhotosAction
} from './routes/object/pages/photos/edit_photos_child';
import ObjectEditField, {action as ObjectEditFieldAction} from './routes/object/pages/details/edit_obj_field';
import ObjectChildren from './routes/object/pages/children/child_objects';
import ChildrenObjEditField, {
    action as ChildrenObjEditFieldAction
} from './routes/object/pages/children/edit_child_field';
import ObjectTariffsAdd, {
    loader as ObjectTariffsAddLoader,
    action as ObjectTariffsAddAction
} from './routes/object/pages/tariffs/new';
import ObjectRulesAdd, {
    loader as ObjectRulesAddLoader,
    action as ObjectRulesAddAction
} from './routes/object/pages/rules/new';
import ObjectChildrenTarrifsAdd, {
    loader as ObjectChildrenTarrifsAddLoader,
    action as ObjectChildrenTarrifsAddAction
} from './routes/object/pages/children_tariffs/new';
import ObjectTariffEdit, {
    loader as ObjectTariffEditLoader,
    action as ObjectTariffEditAction
} from './routes/object/pages/tariffs/edit';
import ObjectChildrenTarrifsEdit, {
    loader as ObjectChildrenTarrifsEditLoader,
    action as ObjectChildrenTarrifsEditAction
} from './routes/object/pages/children_tariffs/edit';
import ObjectRulesEdit, {
    loader as ObjectRulesEditLoader,
    action as ObjectRulesEditAction
} from './routes/object/pages/rules/edit';
import BookingModuleLayout, {loader as BookingModuleLayoutLoader} from './routes/booking_module/layout';
import SearchIndexPage, {loader as SearchIndexPageLoader} from './routes/booking_module/search';
import BookingIndexPage from './routes/booking_module/book_index';
import BookingObjectDetails, {
    loader as BookingObjectDetailsLoader
} from './routes/booking_module/object/object_details';
import MobileEditChildObj, {
    loader as MobileEditChildObjLoader,
    action as MobileEditChildObjAction
} from './routes/mobile/objects/child_obj';
import MobileAddLocation, {
    loader as MobileLocationLoader,
    action as MobileLocationAction
} from './routes/mobile/objects/location';
import MobileObjectDraft, {
    loader as MobileObjectDraftLoader,
    action as MobileObjectDraftAction
} from './routes/mobile/objects/mobileNewObj';
import api from './hooks/useApi';
import BookingForm, {loader as BookingFormLoader, action as BookingFormAction} from './routes/booking_module/book';
import ObjectCalendarBook, {
    loader as ObjectCalendarBookLoader,
    action as ObjectCalendarBookAction
} from './routes/object/pages/calendar_popups/book';
import ProfileEditField from './routes/profile/edit_field';
import BookDetails, {loader as BookDetailsLoader} from './routes/booking_module/book_details';
import BookDetailsOut, {loader as BookDetailsOutLoader} from './routes/booking_module/book_details_out';
import ErrorBoundaryNoDetails from './components/errorBoundaryNoDetails';
import BookingNotFoundError from './components/bookingNotFound';
import ObjectPricesAdd, {
    loader as ObjectPricesAddLoader,
    action as ObjectPricesAddAction
} from './routes/object/pages/tariffs/new_price';
import ObjectPriceEdit, {
    loader as ObjectPricesEditLoader,
    action as ObjectPricesEditAction
} from './routes/object/pages/tariffs/edit_price';
import ObjectCalendarPrice, {
    loader as ObjectCalendarPriceLoader,
    action as ObjectCalendarPriceAction
} from './routes/object/pages/calendar_popups/price';
import {SnackbarProvider, useSnackbar} from "notistack";
import {setSnackbarRef} from "./components/snackbarUtils";
import TechStackButtons from "./routes/docs";
import RedocViewer from "./routes/docs/RedocViewer";


/**
 * To whoever will read this in the future:
 * i was working on this project for like 3-4 months
 * and quit due to lack of terms of reference for 'channel manager' "module"
 * any most of shitcode exists because of that
 * check for the existence of /hotel_front/readme.md file on github.
 * gl.
 */

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#1976d2', // Customize primary color for light mode
        },
        secondary: {
            main: '#f50057', // Customize secondary color for light mode
        },
        backgrounds: {
            main: '#ffffff',
        },
        outlines: {
            main: '#D3D3D3'
        },
        blueButtons: {
            main: '#DCEDFF',
        },
        greyButtons: {
            main: '#CCD1D6',
            hover: '#dadd1e'
        }
        ,
        buttonSuccess: {
            main: '#4BB543'
        },
        buttonError: {
            main: '#ff3333'
        }
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
    },
});

export async function bookDetailsOutAction({request, params}) {
    if (request.method === 'POST') {
        // Проверяем авторизацию пользователя
        const isAuthenticated = await AuthProvider.checkAuth();

        // Если пользователь не авторизован, перенаправляем на страницу логина
        if (!isAuthenticated) {
            const params = new URLSearchParams();
            params.set("from", new URL(window.location.href).pathname);
            return redirect('/login?' + params.toString());
        }

        // Выполните дополнительную логику обработки POST-запроса, если авторизован
        //console.log(`Обрабатываем POST для маршрута с ID: ${params.id}`);
        // Здесь можно добавить любую необходимую логику
        return null; // Возвращаем null после обработки
    }

    // Возвращаем null, если запрос не требует обработки
    return null;
}

function App() {
    return (
        <HelmetProvider>
            <SnackbarProvider maxSnack={3}>
                <AppContent/>
            </SnackbarProvider>
        </HelmetProvider>
    );
}

function AppContent() {
    const {enqueueSnackbar} = useSnackbar(); // Теперь это внутри SnackbarProvider!
    setSnackbarRef(enqueueSnackbar);

    const current_theme = lightTheme;

    const routerjsx = createBrowserRouter(
        createRoutesFromElements(
            <>
                <Route path="/no-nav/new-object/:object_id/child/draft/:child_object_id/" element={<EditChildObj/>}
                       loader={EditChildObjLoader} action={EditChildObjAction}/>
                <Route path="/mobile/no-nav/new-object/:object_id/child/draft/:child_object_id/"
                       element={<MobileEditChildObj/>} loader={MobileEditChildObjLoader}
                       action={MobileEditChildObjAction}/>
                <Route path="/no-nav/new-object/:object_id/location/draft/:child_object_id/"
                       element={<AddLocationToObj/>} loader={AddLocationLoader} action={AddLocationAction}/>
                <Route path="/mobile/no-nav/new-object/:object_id/location/draft/:child_object_id/"
                       element={<MobileAddLocation/>} loader={MobileLocationLoader} action={MobileLocationAction}/>
                <Route path="/no-nav/new-object/:object_id/location/draft/:child_object_id/:update"
                       element={<AddLocationToObj/>} loader={AddLocationLoader} action={AddLocationAction}/>
                <Route path="/mobile/no-nav/new-object/:object_id/location/draft/:child_object_id/:update"
                       element={<MobileAddLocation/>} loader={MobileLocationLoader} action={MobileLocationAction}/>
                <Route path='/book-checkout' element={<BookingForm/>} loader={BookingFormLoader}
                       action={BookingFormAction}
                       errorElement={<ErrorBoundaryNoDetails/>}/>
                <Route path='/book-details/:id' element={<BookDetails/>} loader={BookDetailsLoader}
                       errorElement={<BookingNotFoundError/>}>
                    <Route path='cancel' action={() => {
                        return null;
                    }}/>
                </Route>
                <Route
                    path='/book-details-out/:id'
                    element={<BookDetailsOut/>}
                    loader={BookDetailsOutLoader}
                    action={bookDetailsOutAction} // Подключение action
                    errorElement={<BookingNotFoundError/>}
                >
                    <Route path='cancel' action={() => null}/>
                </Route>
                <Route path='/'
                       exact
                       id='book_layout'
                       element={<BookingModuleLayout/>}
                       loader={BookingModuleLayoutLoader}
                       errorElement={<ErrorBoundaryNoDetails/>}
                >
                    <Route errorElement={<ErrorBoundaryNoDetails/>}>
                        <Route index element={<BookingIndexPage/>}/>
                        <Route path='search' element={<SearchIndexPage/>} loader={SearchIndexPageLoader}/>
                        <Route id='book_obj_details' path='object-details/:object_id' element={<BookingObjectDetails/>}
                               loader={BookingObjectDetailsLoader}>
                        </Route>
                        <Route id='book_hotel_details_slug' path='hotels/:object_slug' element={<BookingObjectDetails/>}
                               loader={BookingObjectDetailsLoader}>
                        </Route>
                    </Route>
                </Route>
                <Route element={<NoMenuLayout/>}>
                    <Route path="profile" element={<Profile/>} loader={ProfileLoader}>
                        <Route
                            path="details"
                            id='profile_details'
                            element={<ProfileDetails/>}
                            loader={ProfileDetailsLoader}
                            action={ProfileDetailsAction}
                        >
                            <Route path=':id/change/:field_name' element={<ProfileEditField/>}>
                                <Route path='submit' action={async ({request, params}) => {
                                    const formData = await request.formData();
                                    const id = params.id;
                                    //console.log({formData, id})
                                    try {
                                        await api.patch(`/api/user/show/${id}/`, formData);
                                    } catch (e) {
                                        return e;
                                    }
                                    return redirect('/profile/details');
                                }}/>
                            </Route>
                        </Route>
                    </Route>
                    <Route
                        element={<Menu/>}
                        loader={MenuLoader}
                        errorElement={<ErrorBoundary/>}
                    >
                        <Route errorElement={<ErrorPage/>}>
                            <Route element={<TestPage/>} loader={TestPageLoader} path='test'/>
                            <Route path='calendar' element={<CalendarPage/>} loader={CalendarLoader}/>
                            <Route path="new-object" element={<NewObject/>} loader={NewObjectLoader}
                                   action={NewObjectAction}/>
                            <Route path="new-object/draft/:object_id" element={<ObjectDraft/>} loader={ObjectDraftLoader}
                                   action={ObjectDraftAction}/>


                            <Route path="owners" element={<Owners/>} loader={OwnersLoader}/>
                            <Route path="docs" element={<TechStackButtons/>} loader={DocsLoader}/>
                            <Route path="docs-viewer" element={<DocsViewer/>} loader={DocsLoaderPage}/>
                            <Route path="front-docs-viewer" element={<FrontDocsViewer/>} loader={FrontDocsLoaderPage}/>
                            <Route path="docs-api-viewer" element={<RedocViewer/>} loader={ApiDocsLoaderPage}/>
                            <Route path="tariffs" element={<Tariffs/>} loader={TariffLoader}>
                                <Route path='new' element={<NewTariff/>} loader={NewTariffLoader}
                                       action={NewTariffAction}/>
                            </Route>
                            <Route path="objects" element={<Objects/>} loader={requirePartnerOrSuperAdmin}/>

                            <Route
                                path="restrictions"
                                element={<Restrictions/>}
                                errorElement={<ErrorPage/>}
                            />
                            <Route
                                path="orders"
                                element={<OrdersPage/>}
                                action={OrdersAction}
                                loader={requirePartnerOrSuperAdmin}
                            >
                                {/* <Route
                path='get-more'
                loader={loaderMore}
              /> */}
                                <Route
                                    path='mutate/:id'
                                    action={mutateOrder}
                                />
                            </Route>

                            <Route
                                element={<SingleObjectLayout/>}
                                loader={SingleObjectLayoutLoader}
                                id='obj_layout'
                                errorElement={<ErrorPage/>}>

                                <Route path="object/:object_id/details" element={<ObjectDetails/>}>
                                    <Route path="edit/:field_name" element={<ObjectEditField/>}
                                           action={ObjectEditFieldAction}/>
                                </Route>

                                <Route path="object/:object_id/tariffs" element={<ObjectTariffs/>}>
                                    <Route path="new" element={<ObjectTariffsAdd/>} loader={ObjectTariffsAddLoader}
                                           action={ObjectTariffsAddAction}/>
                                    <Route path=":rate_id/edit" element={<ObjectTariffEdit/>}
                                           loader={ObjectTariffEditLoader} action={ObjectTariffEditAction}/>
                                    <Route path=":price_id/edit_price" element={<ObjectPriceEdit/>}
                                           loader={ObjectPricesEditLoader} action={ObjectPricesEditAction}/>
                                    <Route path="add_price" element={<ObjectPricesAdd/>} loader={ObjectPricesAddLoader}
                                           action={ObjectPricesAddAction}/>
                                </Route>
                                <Route path="object/:object_id/rules" element={<ObjectRules/>}>
                                    <Route path="new" element={<ObjectRulesAdd/>} loader={ObjectRulesAddLoader}
                                           action={ObjectRulesAddAction}/>
                                    <Route path=":rule_id/edit" element={<ObjectRulesEdit/>}
                                           loader={ObjectRulesEditLoader} action={ObjectRulesEditAction}/>
                                </Route>
                                <Route path="object/:object_id/children_tarrifs" element={<ObjectChildrenTarrifs/>}>
                                    <Route path="new" element={<ObjectChildrenTarrifsAdd/>}
                                           loader={ObjectChildrenTarrifsAddLoader}
                                           action={ObjectChildrenTarrifsAddAction}/>
                                    <Route path=":children_tarrifs_id/edit" element={<ObjectChildrenTarrifsEdit/>}
                                           loader={ObjectChildrenTarrifsEditLoader}
                                           action={ObjectChildrenTarrifsEditAction}/>
                                </Route>
                                <Route path="object/:object_id/calendar" element={<ObjectCalendar/>}
                                       errorElement={<ErrorBoundary/>}>
                                    <Route path='book' element={<ObjectCalendarBook/>} loader={ObjectCalendarBookLoader}
                                           action={ObjectCalendarBookAction}/>
                                    <Route path='price' element={<ObjectCalendarPrice/>}
                                           loader={ObjectCalendarPriceLoader}
                                           action={ObjectCalendarPriceAction}/>
                                    <Route path='close-period' action={async ({request, params}) => {
                                        const formData = await request.formData();
                                        //console.log(formData);
                                        try {
                                            await api.post('/api/booking_modules/use/booking/form/', formData);
                                            return null;
                                        } catch (e) {
                                            return e;
                                        }
                                    }}/>
                                </Route>
                                <Route path="object/:object_id/photos" element={<ObjectPhotos/>}>
                                    <Route path='edit'
                                           element={<EditObjectPhotos/>}
                                           loader={EditObjectPhotosLoader}
                                           action={EditObjectPhotosAction}/>
                                    <Route path='children/:children_object_id/edit'
                                           element={<EditChildObjectPhotos/>}
                                           loader={EditChildObjectPhotosLoader}
                                           action={EditChildObjectPhotosAction}/>
                                </Route>
                                <Route path="object/:object_id/comfortables" element={<ObjectComfortables/>}/>
                                <Route path="object/:object_id/children" element={<ObjectChildren/>}>
                                    <Route path="edit/:children_id/:field_name" element={<ChildrenObjEditField/>}
                                           action={ChildrenObjEditFieldAction}/>
                                </Route>
                                <Route path="object/:object_id/user_rights" element={<ObjectUserRights/>}/>
                                <Route path="*" element={<Page404/>}/>
                            </Route>
                            <Route path="adminH123H" element={<AdminProfile/>} loader={requireSuperAdmin}>
                                <Route index element={<Navigate to="details" replace/>}/>

                                <Route
                                    path="details"
                                    id="admin_profile_details"
                                    element={<ProfileDetails/>}
                                    loader={ProfileDetailsLoader}
                                    action={ProfileDetailsAction}
                                >
                                    <Route path=":id/change/:field_name" element={<ProfileEditField/>}>
                                        <Route
                                            path="submit"
                                            action={async ({request, params}) => {
                                                const formData = await request.formData();
                                                const id = params.id;

                                                try {
                                                    await api.patch(`/api/user/show/${id}/`, formData);
                                                } catch (e) {
                                                    return e;
                                                }

                                                return redirect("/adminH123H/details");
                                            }}
                                        />
                                    </Route>
                                </Route>
                            </Route>
                            <Route
                                path="rights"
                                element={<UserRights/>}
                                loader={UserRightsLoader}
                                action={UserRightsAction}
                            />

                            <Route path="all-users" id='allUsers' element={<AllUsers/>} loader={AllUsersLoader}>
                                <Route path=":user_id/details/edit" element={<EditUserDetails/>}
                                       loader={EditUserDetailsLoader} action={EditUserDetailsAction}>
                                    <Route path="patch"
                                        //TODO: add action here
                                    />

                                </Route>
                                <Route path=":user_id/password" element={<EditUserRights/>}
                                       action={EditUserRightsAction}></Route>
                            </Route>
                            <Route
                                path="new-user"
                                element={<NewUser/>}
                                action={NewUserAction}
                                loader={requireSuperAdmin}
                            />
                            <Route path="new-mail" element={<NewMail/>} action={NewMailAction} loader={NewMailLoader}/>
                            <Route path="all-mailboxes" element={<AllMailboxes/>} loader={AllMailboxesLoader}/>


                        </Route>
                        <Route path="*" element={<Page404/>}/>
                    </Route>
                </Route>


                <Route path="/mobile" element={<MobileMenu/>} errorElement={<ErrorPage/>}>
                    <Route path="objects" element={<MobileList/>} loader={MobileListLoader}/>
                    <Route path="objects/:object_id/calendar" element={<ObjectSingle/>} loader={SingleObjectLoader}/>
                    <Route path="owners" element={<OwnersMobile/>} loader={OwnersMobileLoader}/>
                    <Route path='new-object' loader={async (args) => {
                        const authResult = await requirePartnerOrSuperAdmin(args);
                        if (authResult instanceof Response) return authResult;
                        const response = await api.get('/api/CreateObjForms/HotelObjDraft/');
                        if (!response.data?.id) {
                            throw new Response('Сервер не повернув ID чернетки', {status: 502});
                        }
                        return redirect(`/mobile/new-object/draft/${response.data.id}`);
                    }}/>
                    <Route path='new-object/draft/:object_id' element={<MobileObjectDraft/>}
                           loader={MobileObjectDraftLoader} action={MobileObjectDraftAction}/>
                    <Route path="*" element={<div>not found, 404</div>}/>
                </Route>
                <Route path="/login" element={<LoginPage/>} action={LoginPageAction} loader={LoginPageLoader}
                       errorElement={<ErrorBoundary/>}/>
                <Route path="/adminH123H/publication" element={<PublicationPage/>} loader={requireSuperAdmin}/>
                <Route path="/forbidden" element={<ForbiddenPage/>}/>
                <Route path='/logout' loader={async ({request, params}) => {
                    await AuthProvider.signout();
                    return redirect('/login');
                }}/>
                <Route path="*" element={<Page404/>}/>
            </>
        )
    )
    return (
        <ThemeProvider theme={current_theme}>
            <RouterProvider router={routerjsx}/>
        </ThemeProvider>
    );
}

/*
<div className="App">

          <header className="App-header">
            <p>{message}</p>
          </header>
        </div>
        */
export default App;
